import { FiBook, FiLayers, FiShoppingCart } from 'react-icons/fi';
import { formatBDT } from '@maktech/shared';
import type { Quote } from '@/api/commerce.api';
import { useBooks, useCartQuote } from '@/hooks/api/useCommerceQueries';
import { Modal } from '@/components/common/Modal';
import { DataIcon } from '@/components/common/DataIcon';
import { StateIcon } from '@/components/common/StateIcon';
import { useAuth } from '@/features/auth/AuthContext';
import { useCart } from '@/features/commerce/CartContext';
import { getAppliedCoupon } from '@/features/affiliate/pendingCoupon';

/**
 * Cart — renderCartModal() (:1282) + the v13 books block (:5248).
 *
 * EVERY NUMBER HERE COMES FROM THE SERVER (/cart/quote). Nothing is summed in
 * the browser. That is not fussiness: the prototype computed totals client-side
 * in cartTotals() (:2255), which is exactly where its bundle discount went
 * missing — the display and the charge were two separate calculations, and one
 * of them was wrong. One source, no drift.
 */

export type { Quote };

export function CartModal({
  onClose,
  onCheckoutCourses,
  onCheckoutBooks,
}: {
  onClose: () => void;
  onCheckoutCourses: (quote: Quote, coupon: string | null) => void;
  onCheckoutBooks: () => void;
}) {
  const { courses, books, removeCourse, setBookQty } = useCart();
  const { user } = useAuth();
  const coupon = getAppliedCoupon();

  const { data: quote } = useCartQuote(courses, coupon);

  const { data: bookData } = useBooks(books.length > 0);

  const bookLines = books
    .map((l) => {
      const b = bookData?.books.find((x) => x.slug === l.slug);
      return b ? { ...l, title: b.title, price: b.price } : null;
    })
    .filter((x): x is { slug: string; qty: number; title: string; price: number } => x !== null);

  const booksSubtotal = bookLines.reduce((s, l) => s + l.price * l.qty, 0);
  const DELIVERY = 6000; // ৳60 — the server charges this; shown for parity

  const empty = courses.length === 0 && books.length === 0;

  return (
    <Modal onClose={onClose} label="Your cart">
      <h3 className="mb-2.5">Your cart</h3>
      <div id="cartBody">
        {empty ? (
          <div className="px-2.5 py-[30px] text-center text-[var(--ink-soft)]" data-testid="cart-empty">
            <StateIcon>
              <FiShoppingCart className="size-8" aria-hidden />
            </StateIcon>
            <b>Your cart is empty.</b>
            <p>Add a premium course and it will appear here.</p>
          </div>
        ) : null}

        {quote?.items.map((c) => (
          <div className="flex items-center gap-3 border-b border-dashed border-[var(--line)] py-2.5" key={c.slug} data-testid={`cart-row-${c.slug}`}>
            <div className="grid size-[46px] flex-none place-items-center rounded-[10px] bg-[#1c1c1c] text-white">
              <DataIcon icon={c.icon} className="size-6" />
            </div>
            <div className="flex-1">
              <b className="text-sm">{c.title}</b>
              <div className="text-[12.5px] text-[var(--ink-soft)]">{c.instructor}</div>
            </div>
            <b>{formatBDT(c.price)}</b>
            <button className="inline-flex items-center justify-center rounded-full border-[1.5px] border-[var(--line)] bg-[var(--card)] px-4 py-[9px] text-[13.5px] font-semibold hover:border-[var(--cobalt)] hover:text-[var(--cobalt)]" data-testid={`cart-remove-${c.slug}`} onClick={() => removeCourse(c.slug)}>
              ✕
            </button>
          </div>
        ))}

        {quote && quote.items.length > 0 ? (
          <>
            <div className="mt-3.5 flex justify-between py-[7px] text-[14.5px]">
              <span>Subtotal</span>
              <span data-testid="cart-subtotal">{formatBDT(quote.subtotal)}</span>
            </div>

            {/* The line the prototype never had — the bundle actually coming off. */}
            {quote.bundles.map((b) => (
              <div className="flex justify-between py-[7px] text-[14.5px] text-[var(--teal)]" key={b.slug} data-testid={`cart-bundle-${b.slug}`}>
                <span className="inline-flex items-center gap-1.5">
                  <FiLayers className="size-4 shrink-0" aria-hidden />
                  {b.title} (−{b.discountPct}%)
                </span>
                <span>−{formatBDT(b.discount)}</span>
              </div>
            ))}

            {quote.couponDiscount > 0 ? (
              <div className="flex justify-between py-[7px] text-[14.5px] text-[var(--teal)]" data-testid="cart-coupon-line">
                <span>
                  Coupon {quote.couponCode} (−{quote.couponPct}%)
                </span>
                <span>−{formatBDT(quote.couponDiscount)}</span>
              </div>
            ) : coupon ? (
              <div className="flex justify-between py-[7px] text-[14.5px] text-[var(--teal)]" data-testid="cart-coupon-line">
                <span>Promo {coupon} (applied on course page)</span>
                <span>✓</span>
              </div>
            ) : null}

            <div className="mt-1 flex justify-between border-t border-[var(--line)] py-[7px] text-[17px] font-extrabold">
              <span>Total</span>
              <span data-testid="cart-total">{formatBDT(quote.total)}</span>
            </div>

            <button
              className="mt-3 inline-flex w-full items-center justify-center rounded-full bg-linear-to-br from-[var(--ember)] to-[var(--cobalt)] px-[34px] py-4 text-base font-semibold text-white shadow-[var(--shadow-orange)] hover:-translate-y-0.5"
              data-testid="cart-checkout"
              onClick={() => (user ? onCheckoutCourses(quote, coupon) : alert('Please sign up or log in first'))}
            >
              Checkout {quote.items.length} course{quote.items.length > 1 ? 's' : ''}
            </button>
          </>
        ) : null}

        {/* v13 (:5250) — books ride in the same cart but check out separately. */}
        {bookLines.length ? (
          <div id="cartBooks" className="mt-3.5 grid gap-2 border-t border-dashed border-[var(--line)] pt-3.5">
            <b className="inline-flex items-center gap-1.5 text-[13.5px]">
              <FiBook className="size-4 shrink-0" aria-hidden />
              Books (delivered)
            </b>
            {bookLines.map((l) => (
              <div className="grid grid-cols-[1fr_auto_auto] items-center gap-2.5 border-b border-[var(--line)] pb-1.5 text-[13px]" key={l.slug} data-testid={`cart-book-${l.slug}`}>
                <span>{l.title}</span>
                <span className="flex items-center gap-2">
                  <button className="size-[22px] rounded-md border border-[var(--line)] bg-[var(--card)] leading-none font-extrabold" data-testid={`book-minus-${l.slug}`} onClick={() => setBookQty(l.slug, -1)}>
                    −
                  </button>
                  <b data-testid={`book-qty-${l.slug}`}>{l.qty}</b>
                  <button className="size-[22px] rounded-md border border-[var(--line)] bg-[var(--card)] leading-none font-extrabold" data-testid={`book-plus-${l.slug}`} onClick={() => setBookQty(l.slug, 1)}>
                    +
                  </button>
                </span>
                <span>{formatBDT(l.price * l.qty)}</span>
              </div>
            ))}
            <div className="grid grid-cols-[1fr_auto_auto] items-center gap-2.5 text-[13px]">
              <span className="text-[var(--ink-soft)]">Delivery</span>
              <span />
              <span className="text-[var(--ink-soft)]">{formatBDT(DELIVERY)}</span>
            </div>
            <div className="grid grid-cols-[1fr_auto_auto] items-center gap-2.5 text-[13px] font-extrabold">
              <span>Books total</span>
              <span />
              <span data-testid="books-total">{formatBDT(booksSubtotal + DELIVERY)}</span>
            </div>
            <button
              className="inline-flex w-full items-center justify-center rounded-full border-[1.5px] border-[var(--line)] bg-[var(--card)] px-4 py-[9px] text-[13.5px] font-semibold hover:border-[var(--cobalt)] hover:text-[var(--cobalt)]"
              data-testid="books-checkout"
              onClick={() => (user ? onCheckoutBooks() : alert('Please sign up or log in first'))}
            >
              Checkout books (cash on delivery)
            </button>
            <p className="mt-1.5 text-[11.5px] text-[var(--ink-soft)]">
              Courses and books check out separately — books need a delivery address.
            </p>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
